import React, { useState, useEffect } from "react";
import { Plus, ChevronLeft, ChevronRight, Clock, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { format, addDays, subDays } from "date-fns";
import { ru } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOURS = Array.from({ length: 16 }, (_, i) => i + 6); // 6:00 to 21:00

export default function ScheduleTab({ user }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [scheduleBlocks, setScheduleBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newBlock, setNewBlock] = useState({
    title: "",
    start_time: "09:00",
    end_time: "11:00",
    comment: ""
  });

  const isAdmin = user?.is_admin;

  useEffect(() => {
    fetchSchedule();
  }, [selectedDate]);

  const fetchSchedule = async () => {
    try {
      const token = localStorage.getItem("token");
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const response = await axios.get(`${API}/schedule?date=${dateStr}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setScheduleBlocks(response.data);
    } catch (error) {
      toast.error("Ошибка загрузки расписания");
    } finally {
      setLoading(false);
    }
  };

  const handleAddBlock = async () => {
    if (!newBlock.title || !newBlock.start_time || !newBlock.end_time) {
      toast.error("Заполните все обязательные поля");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/schedule`, {
        title: newBlock.title,
        date: format(selectedDate, "yyyy-MM-dd"),
        start_time: newBlock.start_time,
        end_time: newBlock.end_time,
        comment: newBlock.comment || null
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      toast.success("Тренировка добавлена");
      setShowAddDialog(false);
      setNewBlock({ title: "", start_time: "09:00", end_time: "11:00", comment: "" });
      fetchSchedule();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка добавления");
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/schedule/${blockId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Удалено");
      fetchSchedule();
    } catch (error) {
      toast.error("Ошибка удаления");
    }
  };

  const getBlockPosition = (startTime, endTime) => {
    const [startHour, startMin] = startTime.split(":").map(Number);
    const [endHour, endMin] = endTime.split(":").map(Number);
    
    const startMinutes = (startHour - 6) * 60 + startMin;
    const endMinutes = (endHour - 6) * 60 + endMin;
    
    return {
      top: `${startMinutes}px`,
      height: `${endMinutes - startMinutes}px`
    };
  };

  const getCurrentTimePosition = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    
    if (hours < 6 || hours > 21) return null;
    
    const totalMinutes = (hours - 6) * 60 + minutes;
    return `${totalMinutes}px`;
  };

  const isToday = format(selectedDate, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
  const currentTimePos = isToday ? getCurrentTimePosition() : null;

  return (
    <div data-testid="schedule-tab">
      {/* Date Navigation */}
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSelectedDate(subDays(selectedDate, 1))}
          className="rounded-full"
          data-testid="prev-day-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        
        <div className="text-center">
          <h2 className="font-heading font-semibold text-lg">
            {format(selectedDate, "EEEE", { locale: ru })}
          </h2>
          <p className="text-sm text-apple-text-secondary">
            {format(selectedDate, "d MMMM yyyy", { locale: ru })}
          </p>
        </div>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSelectedDate(addDays(selectedDate, 1))}
          className="rounded-full"
          data-testid="next-day-btn"
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Add Button (Admin Only) */}
      {isAdmin && (
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button
              className="w-full mb-4 h-12 bg-golf-green hover:bg-golf-green-hover rounded-xl"
              data-testid="add-schedule-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Добавить тренировку
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Новая тренировка</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Название</Label>
                <Input
                  value={newBlock.title}
                  onChange={(e) => setNewBlock({ ...newBlock, title: e.target.value })}
                  placeholder="Тренировка на дальнем поле"
                  className="h-12 bg-apple-gray border-transparent rounded-xl"
                  data-testid="input-title"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Начало</Label>
                  <Input
                    type="time"
                    value={newBlock.start_time}
                    onChange={(e) => setNewBlock({ ...newBlock, start_time: e.target.value })}
                    className="h-12 bg-apple-gray border-transparent rounded-xl"
                    data-testid="input-start-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Конец</Label>
                  <Input
                    type="time"
                    value={newBlock.end_time}
                    onChange={(e) => setNewBlock({ ...newBlock, end_time: e.target.value })}
                    className="h-12 bg-apple-gray border-transparent rounded-xl"
                    data-testid="input-end-time"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Комментарий (опционально)</Label>
                <Textarea
                  value={newBlock.comment}
                  onChange={(e) => setNewBlock({ ...newBlock, comment: e.target.value })}
                  placeholder="Тренировка будет на ближнем грине"
                  className="bg-apple-gray border-transparent rounded-xl resize-none"
                  rows={3}
                  data-testid="input-comment"
                />
              </div>
              <Button
                onClick={handleAddBlock}
                className="w-full h-12 bg-golf-green hover:bg-golf-green-hover rounded-xl"
                data-testid="save-schedule-btn"
              >
                Сохранить
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Timeline */}
      <div className="relative bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-golf-green border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="relative" style={{ height: `${HOURS.length * 60}px` }}>
            {/* Hour Lines */}
            {HOURS.map((hour) => (
              <div
                key={hour}
                className="timeline-hour"
                data-time={`${hour.toString().padStart(2, "0")}:00`}
                style={{ top: `${(hour - 6) * 60}px`, position: "absolute", left: 0, right: 0 }}
              >
                <div className="absolute left-12 right-4 top-0 h-px bg-gray-100"></div>
              </div>
            ))}

            {/* Current Time Line */}
            {currentTimePos && (
              <div 
                className="current-time-line" 
                style={{ top: currentTimePos }}
                data-testid="current-time-line"
              />
            )}

            {/* Schedule Blocks */}
            {scheduleBlocks.map((block) => {
              const position = getBlockPosition(block.start_time, block.end_time);
              return (
                <div
                  key={block.block_id}
                  className="schedule-block"
                  style={position}
                  data-testid={`schedule-block-${block.block_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-golf-green text-sm truncate">
                        {block.title}
                      </p>
                      <div className="flex items-center gap-1 text-xs text-golf-green/70 mt-1">
                        <Clock className="w-3 h-3" />
                        <span>{block.start_time} - {block.end_time}</span>
                      </div>
                      {block.comment && (
                        <div className="flex items-start gap-1 text-xs text-golf-green/70 mt-1">
                          <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                          <span className="line-clamp-2">{block.comment}</span>
                        </div>
                      )}
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => handleDeleteBlock(block.block_id)}
                        className="text-red-500 text-xs hover:underline ml-2"
                        data-testid={`delete-block-${block.block_id}`}
                      >
                        Удалить
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Empty State */}
            {scheduleBlocks.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-apple-gray rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-apple-text-tertiary" />
                  </div>
                  <p className="text-apple-text-secondary">Нет тренировок на этот день</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
